import React from 'react';
import {Link} from 'react-router-dom';
import './HowTo.css';

const workflowSteps = [
  {
    number: '01',
    title: 'Prepare your dataset',
    description:
      'Organize your observations as a time series with clear column names and consistent units. Check for missing timestamps, duplicate records, and nonnumeric flow values before uploading.',
    details: [
      'Use CSV format unless an analysis tool specifies otherwise.',
      'Include a date-and-time column and a drainage-flow column.',
      'Keep sampling intervals consistent whenever possible.',
      'Record phosphorus or other water-quality measurements in separate columns.',
    ],
  },
  {
    number: '02',
    title: 'Choose an analysis method',
    description:
      'Select the method that best matches your question. Event delineation identifies individual drainage responses, while baseflow separation estimates slow and event-driven flow components.',
    details: [
      'Use event delineation to identify event start, peak, and end times.',
      'Use baseflow separation to estimate background drainage flow.',
      'Review method assumptions before interpreting the results.',
    ],
  },
  {
    number: '03',
    title: 'Upload and review',
    description:
      'Open the analysis workspace, select your file, and confirm that the application recognizes the correct date, flow, and water-quality columns.',
    details: [
      'Preview the imported records before starting the calculation.',
      'Confirm units and time zones.',
      'Resolve validation warnings rather than ignoring them.',
    ],
  },
  {
    number: '04',
    title: 'Run the analysis',
    description:
      'Set the available method parameters, run the calculation, and inspect the plotted hydrograph and event summary for unexpected behavior.',
    details: [
      'Start with the recommended defaults when you are unsure.',
      'Compare detected events with the original flow record.',
      'Document parameter changes for reproducible analysis.',
    ],
  },
  {
    number: '05',
    title: 'Interpret and export',
    description:
      'Treat the calculated output as analytical evidence rather than a final conclusion. Review event timing, flow components, nutrient loads, and any flagged records before exporting.',
    details: [
      'Check whether identified events are hydrologically reasonable.',
      'Compare results across methods or parameter settings.',
      'Verify exported values before research or management use.',
    ],
  },
];

const sampleDataFiles = [
  {
    title: 'Hourly CSV Data',
    description: 'Example drainage observations recorded at hourly intervals.',
    url: 'https://drive.google.com/file/d/18H-ahk3hIXXOnzp9yw2RZqrXvTrRPvsE/view?usp=drive_link',
  },
  {
    title: 'Daily CSV Data',
    description: 'Example drainage observations summarized by day.',
    url: 'https://drive.google.com/file/d/1co4urztupmOFUuvs_AKCykOYQ9Z4vdJG/view?usp=drive_link',
  },
  {
    title: 'Statistics Data',
    description: 'Sample observed and simulated values for statistical analysis.',
    url: 'https://drive.google.com/file/d/1DGiL25yGRWh1JyOJ9Pb5Ls-jr6xsifGc/view?usp=drive_link',
  },
  {
    title: 'ANOVA Sample Data',
    description: 'Sample data containing three or more numeric comparison groups.',
    url: 'https://drive.google.com/file/d/1XpQ6HJLXp_Gq-PqmcFrox8SpWW3el7sE/view?usp=drive_link',
  },
];

const HowTo = () => (
  <main className="how-to-page">
    <section className="how-to-hero" aria-labelledby="how-to-title">
      <p className="how-to-eyebrow">Getting started</p>
      <h1 id="how-to-title">From drainage data to useful results.</h1>
      <p className="how-to-introduction">
        Follow this workflow to prepare your observations, choose an
        appropriate method, and review the output produced by Event Baseflow
        Lab.
      </p>

      <div className="how-to-actions">
        <Link className="how-to-primary-action" to="/perform-analysis">
          Open analysis tools        </Link>
        <Link
          className="how-to-secondary-action"
          to="/event-delineating-methods"
        >
          Compare methods
        </Link>
      </div>
    </section>

    <section className="how-to-workflow" aria-labelledby="workflow-title">
      <div className="how-to-section-heading">
        <p className="how-to-eyebrow">Recommended workflow</p>
        <h2 id="workflow-title">Five steps to a careful analysis</h2>
      </div>

      <div className="how-to-step-list">
        {workflowSteps.map((step) => (
          <article className="how-to-step" key={step.number}>
            <span className="how-to-step-number" aria-hidden="true">
              {step.number}
            </span>
            <div className="how-to-step-content">
              <h3>{step.title}</h3>
              <p>{step.description}</p>
              <ul>
                {step.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>
    </section>

    <section className="how-to-workflow" aria-labelledby="sample-data-title">
      <div className="how-to-section-heading">
        <p className="how-to-eyebrow">Practice datasets</p>
        <h2 id="sample-data-title">Sample data files</h2>
        <p>
          These synthetic files demonstrate the expected data formats. They do
          not represent real observations and should not be used as research
          data.
        </p>
      </div>

      <div className="how-to-step-list">
        {sampleDataFiles.map((file, index) => (
          <article className="how-to-step" key={file.title}>
            <span className="how-to-step-number" aria-hidden="true">
              {String(index + 1).padStart(2, '0')}
            </span>
            <div className="how-to-step-content">
              <h3>{file.title}</h3>
              <p>{file.description}</p>
              <a
                className="how-to-secondary-action"
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open sample file
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>

    <section className="how-to-guidance">
      <div>
        <p className="how-to-eyebrow">Before you begin</p>
        <h2>Good input makes the results easier to trust.</h2>
      </div>
      <ul className="how-to-tips">
        <li>Keep an untouched copy of the original dataset.</li>
        <li>Write down all units, assumptions, and parameter choices.</li>
        <li>Investigate missing values and extreme observations.</li>
        <li>Use plots to confirm that calculated events match the data.</li>
        <li>
          For statistics, use columns named <code>observed</code> and{' '}
          <code>simulated</code>, unless using the no-header option.
        </li>
        <li>
          For ANOVA, provide at least three numeric columns, with each column
          representing a group.
        </li>
      </ul>
    </section>

    <section className="how-to-cta">
      <div>
        <p className="how-to-eyebrow">Need more guidance?</p>
        <h2>Review common questions or begin with your own dataset.</h2>
      </div>
      <div className="how-to-actions">
        <Link className="how-to-secondary-action" to="/help">
          Visit help
        </Link>
        <Link className="how-to-primary-action" to="/perform-analysis">
          Start an analysis
        </Link>
      </div>
    </section>
  </main>
);

export default HowTo;